import { FINANCIAL_ASSISTANT_ID } from '../assistants/financial';
import { isAssistantHired } from '../assistants/types';
import { buildFinancialSnapshot } from '../simulation/financialSnapshot';
import { cashRunway, healthStatus } from '../simulation/health';
import { clamp } from '../simulation/math';
import type { AiManager, CompanyListing, GameState, ListingApplication } from './types';

const INITIAL_SHARES = 10_000_000;
const LISTING_MIN_WEEKS = 52;
const MIN_ISSUE_RATIO = 0.10;
const MAX_ISSUE_RATIO = 0.30;
export const AI_MIN_FOUNDER_OWNERSHIP = 0.30;
const AI_MAX_ISSUE_RATIO = 0.20;
export const PLAYER_COMPANY_ID = 'player';

function clone<T>(value: T): T {
  return structuredClone(value);
}

function normalizeApplication(application?: ListingApplication): ListingApplication {
  return {
    active: false,
    weeksRemaining: 0,
    suspended: false,
    auditCompleted: false,
    ...application,
  };
}

function trailingReports(state: GameState, weeks: number) {
  const cutoff = state.week - weeks + 1;
  return state.reports.filter((report) => report.week >= cutoff && report.week <= state.week);
}

function sumBy(reports: ReturnType<typeof trailingReports>, key: 'revenue' | 'netProfit' | 'netCashFlow' | 'seats' | 'segmentPassengers'): number {
  return reports.reduce((sum, report) => sum + (report[key] ?? 0), 0);
}

export function annualRevenue(state: GameState): number {
  return sumBy(trailingReports(state, 52), 'revenue');
}

export function annualNetProfit(state: GameState): number {
  return sumBy(trailingReports(state, 52), 'netProfit');
}

export function trailingOperatingCashFlow(state: GameState): number {
  return sumBy(trailingReports(state, 52), 'netCashFlow');
}

export function averageLoadFactor(state: GameState): number {
  const reports = trailingReports(state, 52);
  const seats = sumBy(reports, 'seats');
  const passengers = sumBy(reports, 'segmentPassengers');
  return seats > 0 ? passengers / seats : 0;
}

export function last4QuartersProfitableCount(state: GameState): number {
  const completedQuarters = Math.floor(state.week / 13);
  if (completedQuarters < 1) return 0;
  let profitable = 0;
  for (let i = 0; i < 4; i += 1) {
    const quarterEnd = (completedQuarters - i) * 13;
    const quarterStart = quarterEnd - 12;
    if (quarterStart < 1) break;
    const profit = state.reports
      .filter((report) => report.week >= quarterStart && report.week <= quarterEnd)
      .reduce((sum, report) => sum + report.netProfit, 0);
    if (profit > 0) profitable += 1;
  }
  return profitable;
}

export function companyNetAssets(state: GameState): number {
  return buildFinancialSnapshot(state).netAssets;
}

export function debtRatio(state: GameState): number {
  const snapshot = buildFinancialSnapshot(state);
  if (snapshot.totalAssets <= 0) return 0;
  return snapshot.debt / snapshot.totalAssets;
}

export function hasDefaultedLoan(state: GameState): boolean {
  return state.loans.some((loan) => loan.kind === 'bank' && loan.weeksRemaining <= 0);
}

export function reputationMultiplier(state: GameState): number {
  return 0.8 + clamp((state.reputation - 60) / 35, 0, 1) * 0.4;
}

export function financialRiskDiscount(state: GameState): number {
  const ratio = debtRatio(state);
  if (ratio <= 0.4) return 1;
  const penalty = Math.floor((ratio - 0.4) / 0.1) * 0.05;
  return Math.max(0.75, 1 - penalty);
}

export function companyValuation(state: GameState): number {
  const snapshot = buildFinancialSnapshot(state);
  const assetValue = Math.max(0, snapshot.netAssets);
  const profitValue = Math.max(0, annualNetProfit(state)) * 10;
  const base = Math.max(assetValue, profitValue);
  return Math.round(base * reputationMultiplier(state) * financialRiskDiscount(state));
}

export function currentSharePrice(state: GameState): number {
  if (!state.companyListing.listed || state.companyListing.totalShares <= 0) return 0;
  return state.companyListing.lastValuation / state.companyListing.totalShares;
}

export interface ListedCompany {
  id: string;
  name: string;
  kind: 'player' | 'ai';
  listing: CompanyListing;
}

function isVisibleListing(listing?: CompanyListing): listing is CompanyListing {
  return Boolean(listing?.listed && listing.totalShares > 0 && listing.sharePrice > 0);
}

export function listedCompanies(state: GameState): ListedCompany[] {
  const companies: ListedCompany[] = [];
  if (!state.gameOver && isVisibleListing(state.companyListing)) {
    companies.push({ id: PLAYER_COMPANY_ID, name: state.companyName, kind: 'player', listing: state.companyListing });
  }
  for (const manager of state.aiManagers ?? []) {
    if (manager.bankrupt || !isVisibleListing(manager.companyListing)) continue;
    companies.push({ id: manager.id, name: manager.name, kind: 'ai', listing: manager.companyListing });
  }
  return companies;
}

export function companyListingFor(state: GameState, companyId: string): CompanyListing | undefined {
  if (companyId === PLAYER_COMPANY_ID) return state.companyListing;
  return state.aiManagers.find((manager) => manager.id === companyId)?.companyListing;
}

export function companySharePrice(state: GameState, companyId: string): number {
  const listing = companyListingFor(state, companyId);
  if (!listing?.listed || listing.totalShares <= 0) return 0;
  return listing.sharePrice || listing.lastValuation / listing.totalShares;
}

export function aiCompanyValuation(manager: AiManager): number {
  const aircraftAssets = manager.aircraft.reduce((sum, aircraft) => sum + aircraft.bookValue, 0);
  const debt = (manager.loans ?? []).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  const netAssets = Math.max(0, manager.cash + aircraftAssets - debt);
  const annualProfitValue = Math.max(0, manager.routes
    .filter((route) => route.active)
    .reduce((sum, route) => sum + route.stats.operatingProfit, 0) * 52) * 10;
  const totalAssets = Math.max(1, manager.cash + aircraftAssets);
  const debtRatio = debt / totalAssets;
  const reputationFactor = 0.8 + clamp((manager.reputation - 60) / 35, 0, 1) * 0.4;
  const riskDiscount = debtRatio <= 0.4 ? 1 : Math.max(0.75, 1 - Math.floor((debtRatio - 0.4) / 0.1) * 0.05);
  return Math.round(Math.max(netAssets, annualProfitValue) * reputationFactor * riskDiscount);
}

export function canAiListCompany(manager: AiManager, week: number): boolean {
  if (manager.human || manager.bankrupt || manager.companyListing?.listed || week < LISTING_MIN_WEEKS) return false;
  const activeRoutes = manager.routes.filter((route) => route.active && route.aircraftIds.length > 0).length;
  if (activeRoutes < 2 || manager.reputation < 70 || manager.cash <= 0) return false;
  const aircraftAssets = manager.aircraft.reduce((sum, aircraft) => sum + aircraft.bookValue, 0);
  const debt = (manager.loans ?? []).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  return debt / Math.max(1, manager.cash + aircraftAssets) <= 0.7;
}

export function listAiCompany(manager: AiManager, week: number): void {
  const valuation = aiCompanyValuation(manager);
  const totalShares = INITIAL_SHARES;
  const publicShares = Math.round(totalShares * 0.2);
  const sharePrice = valuation / totalShares;
  manager.companyListing = {
    listed: true,
    listedWeek: week,
    totalShares,
    outstandingShares: publicShares,
    treasuryShares: 0,
    founderShares: totalShares - publicShares,
    sharePrice,
    lastValuation: valuation,
    sharePriceHistory: [{ week, price: sharePrice }],
  };
  manager.cash += Math.round(valuation * 0.2);
}

function aiFounderShares(listing: CompanyListing): number {
  const fallback = Math.round(listing.totalShares * 0.8);
  return Math.max(0, Math.min(listing.totalShares, Math.round(listing.founderShares ?? fallback)));
}

export function maxAiShareIssue(manager: AiManager): number {
  const listing = manager.companyListing;
  if (!listing?.listed || listing.totalShares <= 0 || listing.sharePrice <= 0) return 0;
  const founderShares = aiFounderShares(listing);
  const ownershipLimitedTotal = Math.floor(founderShares / AI_MIN_FOUNDER_OWNERSHIP);
  const ownershipRoom = Math.max(0, ownershipLimitedTotal - listing.totalShares);
  const roundLimit = Math.floor(listing.totalShares * AI_MAX_ISSUE_RATIO);
  return Math.min(roundLimit, ownershipRoom);
}

export function issueAiCompanyShares(manager: AiManager, week: number, requestedQuantity: number): number {
  const listing = manager.companyListing;
  const maxIssue = maxAiShareIssue(manager);
  const quantity = Math.min(maxIssue, Math.floor(Number(requestedQuantity)));
  if (!listing || quantity <= 0) return 0;

  const price = listing.sharePrice || listing.lastValuation / listing.totalShares;
  if (!Number.isFinite(price) || price <= 0) return 0;
  const proceeds = Math.round(price * quantity);
  const totalShares = listing.totalShares + quantity;
  const founderShares = aiFounderShares(listing);
  manager.cash += proceeds;
  const valuation = aiCompanyValuation(manager);
  const sharePrice = valuation / totalShares;
  manager.companyListing = {
    ...listing,
    totalShares,
    outstandingShares: listing.outstandingShares + quantity,
    founderShares,
    sharePrice,
    lastValuation: valuation,
    sharePriceHistory: [
      ...(listing.sharePriceHistory ?? []),
      { week, price: sharePrice },
    ].slice(-104),
  };
  return quantity;
}

export function updateAiCompanySharePrice(manager: AiManager, week: number): void {
  if (!manager.companyListing?.listed || manager.companyListing.totalShares <= 0) return;
  const valuation = aiCompanyValuation(manager);
  const price = valuation / manager.companyListing.totalShares;
  manager.companyListing = {
    ...manager.companyListing,
    sharePrice: price,
    lastValuation: valuation,
    sharePriceHistory: [
      ...(manager.companyListing.sharePriceHistory ?? []),
      { week, price },
    ].slice(-104),
  };
}

export function delistCompany(state: GameState, companyId: string): GameState {
  const next = clone(state);
  const portfolio = { ...(next.playerAssets.stockPortfolio ?? {}) };
  delete portfolio[companyId];
  const portfolioCost = { ...(next.playerAssets.stockPortfolioCost ?? {}) };
  delete portfolioCost[companyId];
  next.playerAssets = { ...next.playerAssets, stockPortfolio: portfolio, stockPortfolioCost: portfolioCost };
  if (companyId === PLAYER_COMPANY_ID) {
    next.companyListing = {
      ...next.companyListing,
      listed: false,
      outstandingShares: 0,
      sharePrice: 0,
    };
    next.playerAssets.shares = 0;
  } else {
    const manager = next.aiManagers.find((item) => item.id === companyId);
    if (manager) manager.companyListing = undefined;
  }
  return next;
}

function updateExternalListing(next: GameState, companyId: string, update: (listing: CompanyListing) => CompanyListing): void {
  if (companyId === PLAYER_COMPANY_ID) {
    next.companyListing = update(next.companyListing);
    return;
  }
  const manager = next.aiManagers.find((item) => item.id === companyId);
  if (!manager?.companyListing) throw new Error('上市公司不存在');
  manager.companyListing = update(manager.companyListing);
}

export function playerBuyCompanyShares(state: GameState, companyId: string, quantity: number): GameState {
  if (companyId === PLAYER_COMPANY_ID) return playerBuyShares(state, quantity);
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const listing = companyListingFor(state, companyId);
  if (!listing?.listed) throw new Error('公司尚未上市');
  const price = companySharePrice(state, companyId);
  const totalCost = price * quantity;
  if (state.playerAssets.cash < totalCost) throw new Error('个人现金不足以买入这些股份');
  if (quantity > listing.outstandingShares) throw new Error('市场流通股不足');

  const next = clone(state);
  updateExternalListing(next, companyId, (current) => ({
    ...current,
    outstandingShares: current.outstandingShares - quantity,
  }));
  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash - totalCost,
    stockPortfolio: {
      ...(next.playerAssets.stockPortfolio ?? {}),
      [companyId]: (next.playerAssets.stockPortfolio?.[companyId] ?? 0) + quantity,
    },
    stockPortfolioCost: {
      ...(next.playerAssets.stockPortfolioCost ?? {}),
      [companyId]: (next.playerAssets.stockPortfolioCost?.[companyId] ?? 0) + totalCost,
    },
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'player_stock_buy',
    cashAmount: 0,
    profitAmount: 0,
    objectId: companyId,
    label: `个人买入 ${companyId} 股票 ${quantity.toLocaleString('zh-CN')} 股`,
  });
  return next;
}

export function playerSellCompanyShares(state: GameState, companyId: string, quantity: number): GameState {
  if (companyId === PLAYER_COMPANY_ID) return playerSellShares(state, quantity);
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const holding = state.playerAssets.stockPortfolio?.[companyId] ?? 0;
  if (holding < quantity) throw new Error('你持有的股份不足');
  const listing = companyListingFor(state, companyId);
  if (!listing?.listed) throw new Error('公司尚未上市');
  const price = companySharePrice(state, companyId);
  const existingCost = state.playerAssets.stockPortfolioCost?.[companyId];
  const averageCost = existingCost !== undefined && holding > 0 ? existingCost / holding : undefined;
  const next = clone(state);
  updateExternalListing(next, companyId, (current) => ({
    ...current,
    outstandingShares: current.outstandingShares + quantity,
  }));
  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash + price * quantity,
    stockPortfolio: {
      ...(next.playerAssets.stockPortfolio ?? {}),
      [companyId]: holding - quantity,
    },
    stockPortfolioCost: {
      ...(next.playerAssets.stockPortfolioCost ?? {}),
      [companyId]: averageCost === undefined ? 0 : Math.max(0, existingCost! - averageCost * quantity),
    },
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'player_stock_sell',
    cashAmount: 0,
    profitAmount: 0,
    objectId: companyId,
    label: `个人卖出 ${companyId} 股票 ${quantity.toLocaleString('zh-CN')} 股`,
  });
  return next;
}

export function updateSharePrice(state: GameState, recordHistory = false): GameState {
  if (!state.companyListing.listed) return state;
  const next = clone(state);
  const valuation = companyValuation(next);
  const price = valuation / next.companyListing.totalShares;
  next.companyListing = {
    ...next.companyListing,
    sharePrice: price,
    lastValuation: valuation,
  };
  if (recordHistory) {
    next.companyListing.sharePriceHistory = [
      ...(next.companyListing.sharePriceHistory ?? []),
      { week: next.week, price },
    ].slice(-104);
  }
  return next;
}

export interface ReadinessBreakdown {
  scale: number;
  profitQuality: number;
  solvency: number;
  operations: number;
  governance: number;
  total: number;
}

export function listingReadinessScore(state: GameState): ReadinessBreakdown & { ok: boolean } {
  const application = normalizeApplication(state.listingApplication);
  const snapshot = buildFinancialSnapshot(state);

  const SCALE_MIN_REVENUE = 300_000_000;
  const SCALE_MIN_NET_ASSETS = 500_000_000;
  let scale = 0;
  if (annualRevenue(state) >= SCALE_MIN_REVENUE || snapshot.netAssets >= SCALE_MIN_NET_ASSETS) {
    scale = 20;
  }

  let profitQuality = 0;
  const profitableQuarters = last4QuartersProfitableCount(state);
  const positiveOCF = trailingOperatingCashFlow(state) > 0;
  if (profitableQuarters >= 3 && positiveOCF) profitQuality = 30;
  else if (profitableQuarters >= 3 || positiveOCF) profitQuality = 15;

  let solvency = 0;
  const runway = cashRunway(state);
  const ratio = debtRatio(state);
  if (runway >= 12 && ratio <= 0.7) solvency = 20;
  else if (runway >= 12 || ratio <= 0.7) solvency = 10;

  let operations = 0;
  if (state.reputation >= 70 && averageLoadFactor(state) >= 0.65) operations = 15;
  else if (state.reputation >= 70 || averageLoadFactor(state) >= 0.65) operations = 8;

  let governance = 0;
  const hasCFO = isAssistantHired(state, FINANCIAL_ASSISTANT_ID);
  if (hasCFO && application.auditCompleted) governance = 15;
  else if (hasCFO || application.auditCompleted) governance = 8;

  const total = scale + profitQuality + solvency + operations + governance;
  return { scale, profitQuality, solvency, operations, governance, total, ok: total >= 75 };
}

export function canListCompany(state: GameState): { ok: boolean; reason?: string } {
  if (state.companyListing.listed) return { ok: false, reason: '公司已经上市' };
  const application = normalizeApplication(state.listingApplication);
  if (application.active) return { ok: false, reason: '上市申请已在进行中' };
  if (state.week < LISTING_MIN_WEEKS) return { ok: false, reason: `需经营满 ${LISTING_MIN_WEEKS} 周` };
  const health = healthStatus(state);
  if (health === 'crisis' || health === 'restructuring') return { ok: false, reason: '公司当前处于危机或重组状态，无法上市' };
  if (hasDefaultedLoan(state)) return { ok: false, reason: '存在逾期贷款，需先清偿或展期' };
  if (!isAssistantHired(state, FINANCIAL_ASSISTANT_ID)) return { ok: false, reason: '需聘请财务总监' };
  if (!application.auditCompleted) return { ok: false, reason: '需先完成上市审计' };
  const readiness = listingReadinessScore(state);
  if (!readiness.ok) return { ok: false, reason: `上市准备度 ${readiness.total} 分，需达到 75 分` };
  return { ok: true };
}

export function auditCost(state: GameState): number {
  return Math.max(500_000, Math.round(companyNetAssets(state) * 0.005));
}

export function listingPreparationCost(state: GameState): number {
  return Math.max(1_000_000, Math.round(companyNetAssets(state) * 0.01));
}

export function completeAudit(state: GameState): GameState {
  const next = clone(state);
  next.listingApplication = normalizeApplication(next.listingApplication);
  if (next.listingApplication.auditCompleted) throw new Error('审计已完成');
  const cost = auditCost(next);
  if (next.cash < cost) throw new Error(`现金不足以支付审计费用，还需 ${(cost - next.cash).toLocaleString('zh-CN')}`);
  next.cash -= cost;
  next.listingApplication.auditCompleted = true;
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'operating_expense',
    cashAmount: -cost,
    profitAmount: -cost,
    objectId: 'audit',
    label: '上市审计费用',
  });
  return next;
}

export function applyForListing(state: GameState): GameState {
  const check = canListCompany(state);
  if (!check.ok) throw new Error(check.reason);
  const next = clone(state);
  const cost = listingPreparationCost(next);
  if (next.cash < cost) throw new Error(`现金不足以支付承销与法律费用，还需 ${(cost - next.cash).toLocaleString('zh-CN')}`);
  next.cash -= cost;
  next.listingApplication = {
    active: true,
    weeksRemaining: 13,
    suspended: false,
    auditCompleted: true,
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'operating_expense',
    cashAmount: -cost,
    profitAmount: -cost,
    objectId: 'listing-prep',
    label: '上市承销与法律费用',
  });
  return next;
}

export function recentConsecutiveNegativeCashFlow(state: GameState, count = 4): boolean {
  const recent = state.reports.slice(-count);
  if (recent.length < count) return false;
  return recent.every((report) => report.netCashFlow < 0);
}

export function consecutiveNegativeCashFlowWeeks(state: GameState): number {
  let count = 0;
  for (let i = state.reports.length - 1; i >= 0; i -= 1) {
    if (state.reports[i].netCashFlow < 0) count += 1;
    else break;
  }
  return count;
}

export function listingSuspensionReason(state: GameState): string | null {
  const health = healthStatus(state);
  if (health === 'crisis' || health === 'restructuring') return '公司健康状况恶化';
  if (cashRunway(state) < 6) return '现金安全期不足 6 周';
  if (hasDefaultedLoan(state)) return '存在逾期贷款';
  if (recentConsecutiveNegativeCashFlow(state)) return '连续 4 周经营现金流为负';
  return null;
}

export function advanceListingApplication(state: GameState): GameState {
  const next = clone(state);
  next.listingApplication = normalizeApplication(next.listingApplication);
  if (!next.listingApplication.active) return next;

  const suspensionReason = listingSuspensionReason(next);
  if (suspensionReason) {
    next.listingApplication.suspended = true;
    next.listingApplication.suspensionReason = suspensionReason;
    return next;
  }

  if (next.listingApplication.suspended) {
    next.listingApplication.suspended = false;
    next.listingApplication.suspensionReason = undefined;
  }

  if (next.listingApplication.weeksRemaining > 0) {
    next.listingApplication.weeksRemaining -= 1;
  }
  return next;
}

export function completeListing(state: GameState, issueRatio: number): GameState {
  const next = clone(state);
  const application = normalizeApplication(next.listingApplication);
  if (next.companyListing.listed) throw new Error('公司已经上市');
  if (!application.active) throw new Error('尚未进入上市辅导期');
  if (application.suspended) throw new Error('上市辅导因风险事项已暂停，需恢复后才能完成');
  if (application.weeksRemaining > 0) throw new Error('上市辅导期尚未结束');
  const ratio = clamp(issueRatio, MIN_ISSUE_RATIO, MAX_ISSUE_RATIO);
  const baseValuation = companyValuation(next);
  const readiness = listingReadinessScore(next).total;
  const ipoPremium = readiness >= 90 ? 1.3 : readiness >= 80 ? 1.2 : readiness >= 75 ? 1.1 : 1.0;
  const valuation = Math.round(baseValuation * ipoPremium);
  const totalShares = INITIAL_SHARES;
  const publicShares = Math.round(totalShares * ratio);
  const founderShares = totalShares - publicShares;
  const proceeds = Math.round(valuation * ratio);

  next.cash += proceeds;
  next.playerAssets = {
    ...next.playerAssets,
    shares: founderShares,
  };
  const sharePrice = valuation / totalShares;
  next.companyListing = {
    listed: true,
    listedWeek: next.week,
    totalShares,
    outstandingShares: publicShares,
    treasuryShares: 0,
    sharePrice,
    lastValuation: valuation,
    sharePriceHistory: [{ week: next.week, price: sharePrice }],
  };
  next.listingApplication = { active: false, weeksRemaining: 0, suspended: false, auditCompleted: false };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'stock_issue',
    cashAmount: proceeds,
    profitAmount: 0,
    objectId: 'ipo',
    label: `IPO 发行 ${(ratio * 100).toFixed(0)}% 股份，融资 ${proceeds.toLocaleString('zh-CN')}`,
  });
  return next;
}

export function issueShares(state: GameState, quantity: number): GameState {
  if (!state.companyListing.listed) throw new Error('公司尚未上市');
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const next = clone(state);
  const price = currentSharePrice(next);
  const proceeds = price * quantity;
  const maxIssue = Math.round(next.companyListing.totalShares * 0.2);
  if (quantity > maxIssue) throw new Error(`单次增发不得超过总股本的 20%（约 ${maxIssue.toLocaleString('zh-CN')} 股）`);

  next.cash += proceeds;
  next.companyListing = {
    ...next.companyListing,
    totalShares: next.companyListing.totalShares + quantity,
    outstandingShares: next.companyListing.outstandingShares + quantity,
    sharePrice: currentSharePrice(next),
    lastValuation: companyValuation(next),
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'stock_issue',
    cashAmount: proceeds,
    profitAmount: 0,
    objectId: 'listing',
    label: `向市场增发 ${quantity.toLocaleString('zh-CN')} 股`,
  });
  return next;
}

export function buybackShares(state: GameState, quantity: number): GameState {
  if (!state.companyListing.listed) throw new Error('公司尚未上市');
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const next = clone(state);
  const price = currentSharePrice(next);
  const totalCost = price * quantity;
  if (next.cash < totalCost) throw new Error('公司现金不足以回购这些股份');
  if (quantity > next.companyListing.outstandingShares) throw new Error('回购数量不得超过市场流通股数');

  next.cash -= totalCost;
  next.companyListing = {
    ...next.companyListing,
    totalShares: next.companyListing.totalShares - quantity,
    outstandingShares: next.companyListing.outstandingShares - quantity,
    sharePrice: currentSharePrice(next),
    lastValuation: companyValuation(next),
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'stock_buyback',
    cashAmount: -totalCost,
    profitAmount: 0,
    objectId: 'listing',
    label: `从市场回购并注销 ${quantity.toLocaleString('zh-CN')} 股`,
  });
  return next;
}

export function playerSellShares(state: GameState, quantity: number): GameState {
  if (!state.companyListing.listed) throw new Error('公司尚未上市');
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const next = clone(state);
  if (next.playerAssets.shares < quantity) throw new Error('你持有的股份不足');
  const price = currentSharePrice(next);
  const proceeds = price * quantity;

  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash + proceeds,
    shares: next.playerAssets.shares - quantity,
  };
  next.companyListing = {
    ...next.companyListing,
    outstandingShares: next.companyListing.outstandingShares + quantity,
    sharePrice: currentSharePrice(next),
    lastValuation: companyValuation(next),
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'player_stock_sell',
    cashAmount: 0,
    profitAmount: 0,
    objectId: 'listing',
    label: `个人出售 ${quantity.toLocaleString('zh-CN')} 股到市场`,
  });
  return next;
}

export function playerBuyShares(state: GameState, quantity: number): GameState {
  if (!state.companyListing.listed) throw new Error('公司尚未上市');
  if (!Number.isFinite(quantity) || quantity <= 0 || Math.round(quantity) !== quantity) throw new Error('请输入有效的股数');
  const next = clone(state);
  const price = currentSharePrice(next);
  const totalCost = price * quantity;
  if (next.playerAssets.cash < totalCost) throw new Error('个人现金不足以买入这些股份');
  if (quantity > next.companyListing.outstandingShares) throw new Error('市场流通股不足');

  next.playerAssets = {
    ...next.playerAssets,
    cash: next.playerAssets.cash - totalCost,
    shares: next.playerAssets.shares + quantity,
  };
  next.companyListing = {
    ...next.companyListing,
    outstandingShares: next.companyListing.outstandingShares - quantity,
    sharePrice: currentSharePrice(next),
    lastValuation: companyValuation(next),
  };
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'player_stock_buy',
    cashAmount: 0,
    profitAmount: 0,
    objectId: 'listing',
    label: `个人从市场买入 ${quantity.toLocaleString('zh-CN')} 股`,
  });
  return next;
}
